// Risk levels for project items
export const RISK_LEVELS = {
  NONE: 'NONE',
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL'
} as const;

export type RiskLevel = typeof RISK_LEVELS[keyof typeof RISK_LEVELS];

// Risk level aliases for backward compatibility
export const RISK_ALIASES = {
  'MODERATE': RISK_LEVELS.MEDIUM,
  'none': RISK_LEVELS.NONE,
  'low': RISK_LEVELS.LOW,
  'medium': RISK_LEVELS.MEDIUM,
  'high': RISK_LEVELS.HIGH,
  'critical': RISK_LEVELS.CRITICAL
} as const;

// Priority levels for project items
export const PRIORITY_LEVELS = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH'
} as const;

export type PriorityLevel = typeof PRIORITY_LEVELS[keyof typeof PRIORITY_LEVELS];

// Status types for project items
export const PROJECT_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  ON_HOLD: 'ON_HOLD'
} as const;

export type ProjectStatus = typeof PROJECT_STATUS[keyof typeof PROJECT_STATUS];

// Risk level options for dropdowns/selects
export const RISK_OPTIONS = [
  { value: RISK_LEVELS.NONE, label: 'None' },
  { value: RISK_LEVELS.LOW, label: 'Low' },
  { value: RISK_LEVELS.MEDIUM, label: 'Medium' },
  { value: RISK_LEVELS.HIGH, label: 'High' },
  { value: RISK_LEVELS.CRITICAL, label: 'Critical' }
];

// Priority level options for dropdowns/selects
export const PRIORITY_OPTIONS = [
  { value: PRIORITY_LEVELS.LOW, label: 'Low' },
  { value: PRIORITY_LEVELS.MEDIUM, label: 'Medium' },
  { value: PRIORITY_LEVELS.HIGH, label: 'High' }
];

// Status options for dropdowns/selects
export const STATUS_OPTIONS = [
  { value: PROJECT_STATUS.NOT_STARTED, label: 'Not Started' },
  { value: PROJECT_STATUS.IN_PROGRESS, label: 'In Progress' },
  { value: PROJECT_STATUS.COMPLETED, label: 'Completed' },
  { value: PROJECT_STATUS.ON_HOLD, label: 'On Hold' }
];

// Human-readable status labels for UI chips and badges, keyed by the raw enum
export const STATUS_LABELS: Record<string, string> = {
  [PROJECT_STATUS.NOT_STARTED]: 'Not Started',
  [PROJECT_STATUS.IN_PROGRESS]: 'In Progress',
  [PROJECT_STATUS.ON_HOLD]: 'On Hold',
  [PROJECT_STATUS.COMPLETED]: 'Completed'
};

// Risk level colors for UI
export const RISK_COLORS = {
  [RISK_LEVELS.NONE]: 'text-gray-500',
  [RISK_LEVELS.LOW]: 'text-green-500',
  [RISK_LEVELS.MEDIUM]: 'text-yellow-500',
  [RISK_LEVELS.HIGH]: 'text-red-500',
  [RISK_LEVELS.CRITICAL]: 'text-red-600'
};

// Priority level colors for UI
export const PRIORITY_COLORS = {
  [PRIORITY_LEVELS.LOW]: 'text-green-500',
  [PRIORITY_LEVELS.MEDIUM]: 'text-yellow-500',
  [PRIORITY_LEVELS.HIGH]: 'text-red-500'
};

// Risk level utilities
export const normalizeRiskLevel = (risk: string | null | undefined): RiskLevel => {
  if (!risk) return RISK_LEVELS.NONE;
  
  const normalized = risk.toUpperCase().trim();
  
  // Check aliases first
  if (RISK_ALIASES[normalized as keyof typeof RISK_ALIASES]) {
    return RISK_ALIASES[normalized as keyof typeof RISK_ALIASES];
  }
  
  // Check direct matches
  if (Object.values(RISK_LEVELS).includes(normalized as RiskLevel)) {
    return normalized as RiskLevel;
  }
  
  // Default to NONE for unknown values
  return RISK_LEVELS.NONE;
};

export const getRiskLevelPriority = (risk: RiskLevel): number => {
  const priorities = {
    [RISK_LEVELS.NONE]: 0,
    [RISK_LEVELS.LOW]: 1,
    [RISK_LEVELS.MEDIUM]: 2,
    [RISK_LEVELS.HIGH]: 3,
    [RISK_LEVELS.CRITICAL]: 4
  };
  return priorities[risk] || 0;
};

// A non-completed item counts toward the risk filter when its risk matches the
// selected level. Shared by every "does this item match the filter" check below.
const itemMatchesRiskFilter = (item: any, normalizedFilter: string): boolean =>
  item.status !== PROJECT_STATUS.COMPLETED &&
  normalizeRiskLevel(item.risks).toLowerCase() === normalizedFilter;

// Check if a project has at least one non-completed item matching the selected risk level.
// Shared by the Projects, Archived Projects, and Map views so the risk filter behaves
// identically everywhere.
export const projectMatchesRiskFilter = (project: any, riskFilter: string): boolean => {
  if (!riskFilter || riskFilter === 'all') return true;

  const normalizedFilter = normalizeRiskLevel(riskFilter).toLowerCase();
  const matches = (item: any) => itemMatchesRiskFilter(item, normalizedFilter);

  return (project.items || []).some(matches) || (project.actionItems || []).some(matches);
};

// Count a project's non-completed items matching the selected risk level — used for the
// map pin badge, so it agrees with projectMatchesRiskFilter and the project card popup.
export const countMatchingRiskItems = (project: any, riskFilter: string): number => {
  if (!riskFilter || riskFilter === 'all') return 0;

  const normalizedFilter = normalizeRiskLevel(riskFilter).toLowerCase();
  const matches = (item: any) => itemMatchesRiskFilter(item, normalizedFilter);

  return [...(project.items || []), ...(project.actionItems || [])].filter(matches).length;
};

export const getHighestRiskLevel = (riskLevels: string[]): RiskLevel => {
  if (!riskLevels || riskLevels.length === 0) {
    return RISK_LEVELS.NONE;
  }
  
  const normalizedLevels = riskLevels.map(level => normalizeRiskLevel(level));
  const highestPriority = Math.max(...normalizedLevels.map(level => getRiskLevelPriority(level)));
  
  return Object.values(RISK_LEVELS).find(level => getRiskLevelPriority(level) === highestPriority) || RISK_LEVELS.NONE;
}; 